import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import Pagination from '../components/Pagination';
import SortIcon from '../components/SortIcon';

/**
 * InvoicesList page - displays all invoices with sorting, filtering, and bulk actions.
 *
 * @returns {JSX.Element} The invoices list page
 */
function InvoicesList() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState('invoice_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [syncStatus, setSyncStatus] = useState(null);
  const [filters, setFilters] = useState({
    business_id: searchParams.get('business_id') || '',
    vendor_id: searchParams.get('vendor_id') || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const loadBusinesses = useCallback(async () => {
    try {
      const response = await api.getBusinesses();
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
      setToast({ message: 'Failed to load businesses', type: 'error' });
    }
  }, []);

  const loadVendors = useCallback(async () => {
    try {
      // Ruth UAT 2026-05-28 (GH #162): the filter dropdown must show EVERY
      // vendor the user has invoices for, including inactive ones (so they
      // can filter historical invoices by a vendor that's since been
      // deactivated). Without include_inactive=true, vendors with status
      // != 'active' silently disappear from the dropdown.
      const response = await api.getVendors({ include_inactive: true });
      setVendors(response.data);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setToast({ message: 'Failed to load vendors', type: 'error' });
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (filters.business_id) params.business_id = filters.business_id;
      if (filters.vendor_id) params.vendor_id = filters.vendor_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await api.getInvoices(params);
      setInvoices(response.data);
      setIsLoading(false);
      setIsInitialLoad(false);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setLoadError('Failed to load invoices');
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadBusinesses();
    loadVendors();
    loadInvoices();
  }, [loadBusinesses, loadVendors, loadInvoices]);

  // Sync filters from URL search params (e.g. navigating from Analytics vendor link)
  useEffect(() => {
    const newFilters = {
      business_id: searchParams.get('business_id') || '',
      vendor_id: searchParams.get('vendor_id') || '',
      start_date: searchParams.get('start_date') || '',
      end_date: searchParams.get('end_date') || ''
    };
    setFilters(prev => {
      if (prev.business_id === newFilters.business_id &&
          prev.vendor_id === newFilters.vendor_id &&
          prev.start_date === newFilters.start_date &&
          prev.end_date === newFilters.end_date) {
        return prev;
      }
      return newFilters;
    });
  }, [searchParams]);

  // Open invoice detail modal when navigated back from edit with state
  useEffect(() => {
    if (location.state?.openInvoiceId) {
      setSelectedInvoiceId(location.state.openInvoiceId);
      setShowDetailModal(true);
      if (location.state.toast) {
        setToast(location.state.toast);
      }
      // Clear the state so refreshing doesn't re-open the modal
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleDelete = (id) => {
    setConfirmModal({
      title: 'Delete Invoice',
      message: 'Delete this invoice?',
      description: 'This will permanently remove the invoice and all its line items.',
      confirmLabel: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.deleteInvoice(id);
          loadInvoices();
        } catch (error) {
          console.error('Error deleting invoice:', error);
          setToast({ message: 'Error deleting invoice', type: 'error' });
        }
      },
    });
  };

  const handleToggleStatus = async (id) => {
    try {
      await api.toggleInvoiceStatus(id);
      loadInvoices();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  /**
   * Handles clicking a sortable column header. Toggles direction if same field,
   * otherwise sets new field with ascending default.
   *
   * @param {string} field - The invoice field name to sort by
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Returns a styled sort indicator element for a column header.
   *
   * @param {string} field - The field name to check
   * @returns {JSX.Element|null} Sort indicator span or null when inactive
   */
  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <SortIcon direction={sortDirection} />;
  };

  /**
   * Syncs all line item categories from the SKU catalog.
   * Shows the number of updated items and reloads invoices to reflect changes.
   */
  const handleSyncCategories = async () => {
    setSyncStatus('syncing');
    try {
      const res = await api.syncLineItemCategories();
      const { updated, skus_created, total_uncategorized_after } = res.data;
      if (updated > 0 || skus_created > 0) {
        const parts = [];
        if (updated > 0) parts.push(`${updated} line item${updated !== 1 ? 's' : ''} categorized`);
        if (skus_created > 0) parts.push(`${skus_created} SKU${skus_created !== 1 ? 's' : ''} added to catalog`);
        if (total_uncategorized_after > 0) parts.push(`${total_uncategorized_after} still uncategorized`);
        setToast({ message: parts.join(', '), type: 'success' });
        loadInvoices();
      } else {
        setToast({ message: 'No uncategorized line items with matching SKU catalog categories found', type: 'warning' });
      }
      setSyncStatus(null);
    } catch (error) {
      console.error('Error syncing categories:', error);
      setToast({ message: 'Failed to sync categories', type: 'error' });
      setSyncStatus(null);
    }
  };

  /**
   * Filtered and sorted invoices computed from current state.
   * Applies text search filter then sorts by the active sort field/direction.
   */
  const filteredAndSortedInvoices = useMemo(() => {
    let result = invoices;

    // Text search filter
    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      result = result.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(query) ||
        (inv.vendor_name || '').toLowerCase().includes(query) ||
        (inv.business_name || '').toLowerCase().includes(query)
      );
    }

    // Sort
    const sorted = [...result].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'invoice_number':
          valA = (a.invoice_number || '').toLowerCase();
          valB = (b.invoice_number || '').toLowerCase();
          break;
        case 'vendor_name':
          valA = (a.vendor_name || '').toLowerCase();
          valB = (b.vendor_name || '').toLowerCase();
          break;
        case 'invoice_date':
          valA = a.invoice_date || '';
          valB = b.invoice_date || '';
          break;
        case 'total_amount':
          valA = a.total_amount || 0;
          valB = b.total_amount || 0;
          break;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [invoices, searchText, sortField, sortDirection]);

  /**
   * Paginated slice of filtered/sorted invoices for current page.
   */
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return filteredAndSortedInvoices.slice(startIndex, endIndex);
  }, [filteredAndSortedInvoices, currentPage, perPage]);

  /** Sum of total_amount for all currently filtered invoices. */
  const filteredTotal = useMemo(() => {
    return filteredAndSortedInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  }, [filteredAndSortedInvoices]);

  /**
   * Reset to page 1 when filters change.
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filters]);

  if (isLoading && isInitialLoad) {
    return (
      <div className="page">
        <h1>All Invoices</h1>
        <Loading variant="spinner" message="Loading invoices..." />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="page">
        <h1>All Invoices</h1>
        <div className="load-error">
          <p className="load-error__message">{loadError}</p>
          <button className="load-error__retry" onClick={loadInvoices}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          <div>
            <h1 className="page-title">All Invoices</h1>
            <p className="page-subtitle">View and manage all your recorded invoices</p>
            <p className="invoices-summary">
              {filteredAndSortedInvoices.length} invoice{filteredAndSortedInvoices.length !== 1 ? 's' : ''} | Total: ${filteredTotal.toFixed(2)}
            </p>
          </div>
          {invoices.length > 0 && (
            <button
              className="btn-small btn-secondary"
              onClick={handleSyncCategories}
              disabled={syncStatus === 'syncing'}
              title="Sync line item categories from the SKU catalog"
            >
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync All Categories'}
            </button>
          )}
        </div>
        {/* Sync feedback now shown via Toast notifications */}
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="field">
          <label className="label" htmlFor="invoices-search">Search</label>
          <input
            id="invoices-search"
            className="input"
            type="text"
            placeholder="Search invoice #, vendor, business..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label">Business</label>
          <select
            className="input"
            value={filters.business_id}
            onChange={(e) => setFilters({ ...filters, business_id: e.target.value })}
          >
            <option value="">All Businesses</option>
            {businesses.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Vendor</label>
          <select
            className="input"
            value={filters.vendor_id}
            onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })}
          >
            <option value="">All Vendors</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="invoices-start-date">Start Date</label>
          <input
            id="invoices-start-date"
            className="input"
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="invoices-end-date">End Date</label>
          <input
            id="invoices-end-date"
            className="input"
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
          />
        </div>
      </div>

      {/* Invoices Table */}
      <div className={`card${isLoading ? ' skeleton-overlay' : ''}`}>
        {filteredAndSortedInvoices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">📋</div>
            <div className="empty-state__title">No invoices found</div>
            <div className="empty-state__description">
              Try adjusting your filters or add a new invoice to get started.
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', flexWrap: 'wrap', marginTop: 'var(--space-4)' }}>
              <Link to="../invoice-entry"><button type="button" className="btn btn-primary">Enter Invoice Manually</button></Link>
              <Link to="../ocr"><button type="button" className="btn btn-secondary">Upload Invoice PDF</button></Link>
            </div>
          </div>
        ) : (
          <>
            <div className="tbl-wrap tbl-wrap--lg">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="sort" onClick={() => handleSort('invoice_date')} aria-sort={sortField === 'invoice_date' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      Date{getSortIndicator('invoice_date')}
                    </th>
                    <th scope="col" className="sort" onClick={() => handleSort('invoice_number')} aria-sort={sortField === 'invoice_number' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      Invoice #{getSortIndicator('invoice_number')}
                    </th>
                    <th scope="col">Business</th>
                    <th scope="col" className="sort" onClick={() => handleSort('vendor_name')} aria-sort={sortField === 'vendor_name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      Vendor{getSortIndicator('vendor_name')}
                    </th>
                    <th scope="col">Subtotal</th>
                    <th scope="col">Tax</th>
                    <th scope="col" title="Fuel Surcharge">F.S.</th>
                    <th scope="col" className="sort" onClick={() => handleSort('total_amount')} aria-sort={sortField === 'total_amount' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      Total{getSortIndicator('total_amount')}
                    </th>
                    <th scope="col" className="sort" onClick={() => handleSort('status')} aria-sort={sortField === 'status' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      Status{getSortIndicator('status')}
                    </th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{inv.invoice_date}</td>
                      <td>{inv.invoice_number || '-'}</td>
                      <td>{inv.business_name}</td>
                      <td>{inv.vendor_name}</td>
                      <td><span className="td-amt">${(inv.subtotal || 0).toFixed(2)}</span></td>
                      <td><span className="td-amt">${(inv.tax || 0).toFixed(2)}</span></td>
                      <td><span className="td-amt">${(inv.fuel_surcharge || 0).toFixed(2)}</span></td>
                      <td><strong><span className="td-amt">${inv.total_amount.toFixed(2)}</span></strong></td>
                      <td>
                        <span className={`badge status-${inv.status}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>
                        <div className="settings-form__action-btns">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setSelectedInvoiceId(inv.id); setShowDetailModal(true); }}
                            title="View invoice details and line items"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleToggleStatus(inv.id)}
                            title={inv.status === 'active' ? 'Archive this invoice' : 'Restore this invoice'}
                            style={{ color: inv.status === 'active' ? 'var(--warning)' : 'var(--success)' }}
                          >
                            {inv.status === 'active' ? 'Archive' : 'Restore'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDelete(inv.id)}
                            title="Delete this invoice permanently"
                            style={{ color: 'var(--error)' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={filteredAndSortedInvoices.length}
              currentPage={currentPage}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      <InvoiceDetailModal
        invoiceId={selectedInvoiceId}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        invoices={filteredAndSortedInvoices}
        currentIndex={filteredAndSortedInvoices.findIndex(inv => inv.id === selectedInvoiceId)}
        onNavigate={(newIndex) => {
          if (filteredAndSortedInvoices[newIndex]) {
            setSelectedInvoiceId(filteredAndSortedInvoices[newIndex].id);
          }
        }}
      />
    </div>
  );
}

export default InvoicesList;
